import { useEffect, useState } from 'react'
import ModalComponent from '..'
import { calculateProductPrice, deepCopy } from '../../../helpers'
import { useGetAllProductsQuery } from '../../../redux-toolkits/products/products.slice'
import { ProductSearchQuery } from '../../../redux-toolkits/products/products.type'
import { Product } from '../../../redux-toolkits/products/products.type'
import { Button } from '../../button'
import Icon from '../../icon'
import { Checkbox } from '../../inputs/Checkbox'
import { SearchInput } from '../../inputs/searchInput'
import { TableComponent } from '../../table'

const defaultquery = {
  search: undefined,
  skip: 0,
  limit: 20,
}

type V = {
  product: Product
  quantity: number
}

export const SelectProductQuantity = ({
  maxSelection,
  open,
  products,
  setProducts,
  query,
  excludedProducts,
}: {
  maxSelection?: number
  open?: boolean
  products: V[]
  setProducts: (products: V[]) => void
  query?: ProductSearchQuery
  excludedProducts?: string[]
}) => {
  const [queryParam, setQueryParam] = useState<ProductSearchQuery>(defaultquery)

  const { data, isFetching } = useGetAllProductsQuery(
    {
      ...query,
      ...queryParam,
    },
    {
      skip: !open,
      refetchOnMountOrArgChange: true,
    },
  )

  return (
    <div
      className="
        flex flex-col gap-2
        cursor-pointer
        "
    >
      <div className="bg-white pb-2 !max-w-full sticky top-0">
        <SearchInput
          searchFunction={(search) => {
            setQueryParam({
              ...queryParam,
              search: search === '' ? undefined : search,
              skip: 0,
            })
          }}
          placeholder="Search products"
          className="!max-w-full"
        />
      </div>

      <div className="flex-1 overflow-y-scroll">
        <TableComponent
          rows={(data?.data?.data ?? [])
            .filter((i) => !excludedProducts?.includes(i._id))
            .map((item) => {
              const existingSelection = products.find(
                (p) => p.product._id === item._id,
              )

              return {
                id: item._id,
                content: [
                  <Checkbox
                    className="!gap-6 !w-full"
                    disabled={
                      maxSelection !== undefined &&
                      products.length >= maxSelection &&
                      !products.map((c) => c.product._id).includes(item._id)
                    }
                    checkStatus={
                      products.map((c) => c.product._id)?.includes(item._id) ??
                      false
                    }
                    handleChange={(checked) => {
                      if (checked) {
                        const dataCopy = deepCopy(products)

                        dataCopy.push({
                          product: item,
                          quantity: 1,
                        })

                        setProducts(dataCopy)
                      } else {
                        const dataCopy = deepCopy(products)

                        setProducts(
                          dataCopy.filter(
                            (singleData) => item._id !== singleData.product._id,
                          ),
                        )
                      }
                    }}
                    key={item._id}
                    text={
                      <div
                        key={item._id}
                        className="flex gap-3 first:pt-0 items-center"
                      >
                        <img
                          alt={item.name}
                          src={item.images[0]}
                          className="min-w-[60px] max-w-[60px] min-h-[60px] max-h-[60px] aspect-square object-cover rounded-md overflow-hidden "
                        />

                        <div className="flex-1">
                          <p className="flex-1 w-fit text-base text-black font-normal">
                            {item.name}
                          </p>

                          <p className="text-sm text-sec-black">
                            {calculateProductPrice(item)}
                          </p>
                        </div>

                        {existingSelection ? (
                          <div className="flex items-center gap-4">
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.stopPropagation()
                                e.preventDefault()

                                const dataCopy = deepCopy(products)

                                setProducts(
                                  dataCopy.map((i) => {
                                    return i.product._id ===
                                      existingSelection.product._id
                                      ? {
                                          product: i.product,
                                          quantity: i.quantity - 1,
                                        }
                                      : i
                                  }),
                                )
                              }}
                              className="disabled:opacity-20 disabled:cursor-not-allowed w-fit"
                              disabled={existingSelection.quantity <= 1}
                            >
                              <Icon id="minus-circle" width={24} height={24} />
                            </button>

                            <p>{existingSelection.quantity}</p>

                            <button
                              type="button"
                              onClick={async (e) => {
                                e.stopPropagation()
                                e.preventDefault()

                                const dataCopy = deepCopy(products)

                                setProducts(
                                  dataCopy.map((i) => {
                                    return i.product._id ===
                                      existingSelection.product._id
                                      ? {
                                          product: i.product,
                                          quantity: i.quantity + 1,
                                        }
                                      : i
                                  }),
                                )
                              }}
                              className="disabled:opacity-20 disabled:cursor-not-allowed w-fit"
                              disabled={false}
                            >
                              <Icon id="plus-circle" width={24} height={24} />
                            </button>
                          </div>
                        ) : undefined}
                      </div>
                    }
                  />,
                ],
              }
            })}
          loading={isFetching}
          isEmpty={[0, undefined].includes(data?.data?.data?.length)}
        />
      </div>
    </div>
  )
}

export const SelectProductQuantityModal = ({
  open,
  setOpen,
  onCloseCallback,
  onSuccessCallback,
  selectedProducts,
  maxSelection,
  loading = false,
  query,
  excludedProducts,
}: {
  open: boolean
  setOpen: (val: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (val: V[]) => void
  selectedProducts?: V[]
  maxSelection?: number
  loading?: boolean
  query?: ProductSearchQuery
  excludedProducts?: string[]
}) => {
  const [products, setProducts] = useState<V[]>([])

  useEffect(() => {
    if (open) {
      setProducts(selectedProducts ?? [])
    }
  }, [open])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        onCloseCallback && onCloseCallback()
      }}
      title="Select Products"
      childrenClassName="md:!max-w-[420px]"
    >
      <div className="flex flex-col gap-6">
        <SelectProductQuantity
          maxSelection={maxSelection}
          open={open}
          products={products}
          setProducts={setProducts}
          query={query}
          excludedProducts={excludedProducts}
        />

        <Button
          type="button"
          primary
          label="Continue"
          onClick={() => {
            onSuccessCallback && onSuccessCallback(products)
          }}
          loading={loading}
          disabled={loading || products.length < 1}
          className="sticky bottom-0"
        />
      </div>
    </ModalComponent>
  )
}
